// Карточка заказчика. Отдельного адреса «один заказчик» в контракте нет, поэтому строка
// берётся из того же списка, что и в колонке слева: react-query отдаёт его из кэша, второго
// запроса не уходит.
//
// Заказчика открывают ради одного вопроса — какие у него объекты, поэтому главное действие
// карточки ведёт в реестр объектов с уже наложенным отбором.

import { RiBriefcaseLine, RiBuilding4Line, RiPencilLine } from '@remixicon/react';
import { Link } from 'react-router';

import type { ApiError } from '@/shared/errors/api-error';
import { DetailPane } from '@/layout/DetailPane';
import { useAuthStore } from '@/shared/auth/store';
import { can } from '@/shared/rbac/permissions';
import * as Button from '@/shared/ui/button';
import { EmptyState, ErrorState, ScreenState, Skeleton } from '@/shared/ui/screen-state';

import { countLabel } from '@/shared/text/plural';
import { formatPhone } from '@/shared/text/phone';
import { formatDateOnly } from '@/shared/time/format';
import { FieldList, FieldRow } from '@/shared/ui/field-row';
import { useContractsQuery, useCustomersQuery, useSitesQuery } from '@/shared/org/queries';
import type { ContractOut, CustomerOut } from '@/shared/api/endpoints/org';
import type { SitesRoute } from '../route';
import {
  FILTER_CUSTOMER,
  openEditContractHref,
  openEditCustomerHref,
  openNewSiteHref,
  sitesHref,
} from '../route';

const OBJECT_FORMS = ['объект', 'объекта', 'объектов'] as const;

/** Правка справочников — то же право, что и заведение (server/app/api/v1/dictionaries.py). */
const EDIT_PERMISSION = 'core.dictionary.edit';

export function CustomerCard({ customerId, route }: { customerId: string; route: SitesRoute }) {
  const query = useCustomersQuery({ q: route.search.q });
  const customer = query.data?.items.find((item) => item.id === customerId);
  const правитьМожно = can(useAuthStore((state) => state.permissions), EDIT_PERMISSION);

  return (
    <DetailPane
      title={
        <span className='flex items-center gap-2'>
          <RiBriefcaseLine className='size-5 shrink-0 text-text-soft-400' />
          <span className='truncate'>{customer?.name ?? 'Заказчик'}</span>
        </span>
      }
      actions={
        customer === undefined ? undefined : (
          <>
            {/* Правка заказчика появилась 15.09.2026: до неё опечатку в ИНН или телефоне нельзя
                было исправить ничем, кроме как завести заказчика заново — то есть оставить в
                справочнике двойника, к которому привязана половина договоров. */}
            {правитьМожно && (
              <Button.Root variant='neutral' mode='stroke' size='xsmall' asChild>
                <Link to={openEditCustomerHref(route, customer.id)}>
                  <Button.Icon as={RiPencilLine} />
                  Править
                </Link>
              </Button.Root>
            )}
            <Button.Root variant='primary' mode='stroke' size='xsmall' asChild>
              <Link
                to={sitesHref('objects', {
                  ...route.search,
                  q: undefined,
                  tab: undefined,
                  filters: { [FILTER_CUSTOMER]: customer.id },
                })}
              >
                <Button.Icon as={RiBuilding4Line} />
                Объекты заказчика
              </Link>
            </Button.Root>
          </>
        )
      }
    >
      <ScreenState
        data={query.data}
        error={query.error}
        subject='карточка заказчика'
        isEmpty={() => customer === undefined}
        onRetry={() => void query.refetch()}
        loading={
          <div className='flex flex-col gap-3 p-5'>
            <Skeleton className='h-24 w-full' />
          </div>
        }
        empty={
          <EmptyState
            icon={RiBriefcaseLine}
            title='Этого заказчика нет в текущей выборке'
            description='Ссылка ведёт на заказчика, который не попал в список из-за поиска — либо его завели после того, как страница открылась. Очистите поиск, и он появится.'
          />
        }
      >
        {() =>
          customer === undefined ? null : (
            <div className='flex flex-col gap-4 p-5'>
              <FieldList title='Чем занимается'>
                <FieldRow
                  label='Отрасль'
                  value={customer.industry ?? null}
                  missing='отрасль не указана'
                />
                <FieldRow
                  label='Регион'
                  value={customer.region ?? null}
                  missing='регион не указан'
                />
                <FieldRow label='ИНН' value={customer.inn ?? null} missing='ИНН не указан' />
              </FieldList>
              <FieldList title='С кем связываться'>
                <FieldRow
                  label='Контактное лицо'
                  value={customer.contact_person ?? null}
                  missing='контактное лицо не указано'
                />
                <FieldRow
                  label='Телефон'
                  value={formatPhone(customer.contact_phone)}
                  missing='телефон не указан'
                />
                <FieldRow
                  label='Состояние'
                  value={customer.is_active ? 'действует' : 'не действует'}
                />
              </FieldList>
              <ДоговорыИОбъекты customer={customer} route={route} />

              <div>
                <Button.Root variant='neutral' mode='stroke' size='xsmall' asChild>
                  <Link
                    to={openNewSiteHref({
                      ...route,
                      search: { ...route.search, filters: { [FILTER_CUSTOMER]: customer.id } },
                    })}
                  >
                    <Button.Icon as={RiBuilding4Line} />
                    Новый объект этого заказчика
                  </Link>
                </Button.Root>
              </div>
            </div>
          )
        }
      </ScreenState>
    </DetailPane>
  );
}

/**
 * Договоры заказчика и его объекты.
 *
 * Тз, раздел 5.2, называет карточку заказчика как «контакты, договоры, объекты». Договоров и
 * объектов в ней не было: заказчик открывался, а на вопрос «что у него есть» карточка не
 * отвечала — при том, что объект без договора этого заказчика не заводится, а какой договор
 * действует, видно только отсюда.
 *
 * Отдельной карточки договора нет намеренно: у него шесть полей, и вынести их в свой экран
 * значит завести ещё одну поверхность ради шести строк, которые целиком помещаются здесь.
 */
function ДоговорыИОбъекты({ customer, route }: { customer: CustomerOut; route: SitesRoute }) {
  const договоры = useContractsQuery({ customer_id: customer.id });
  const объекты = useSitesQuery({ customer_id: customer.id });
  const правитьМожно = can(useAuthStore((state) => state.permissions), EDIT_PERMISSION);

  return (
    <>
      <FieldList title='Договоры'>
        {/* Загрузка — скелет, а не слово «загружаем» строкой с тем же заголовком: подпись,
            повторяющая заголовок блока, читалась как второй факт. Ошибка — с повтором. */}
        {договоры.isPending ? (
          <Skeleton className='h-9' />
        ) : договоры.isError ? (
          <ErrorState
            subject='список договоров'
            error={договоры.error as ApiError}
            onRetry={() => void договоры.refetch()}
          />
        ) : (договоры.data?.items ?? []).length === 0 ? (
          <FieldRow
            label='Договоров нет'
            value={null}
            // Не пустая строка: без договора объекты этого заказчика завести нельзя, и об этом
            // человек узнает не здесь, а через неделю, на мастере объекта.
            missing='объект привязывается к договору, поэтому объекты этого заказчика завести не выйдет'
          />
        ) : (
          (договоры.data?.items ?? []).map((договор) => (
            <FieldRow
              key={договор.id}
              label={договор.number === null ? 'Без номера' : `№ ${договор.number}`}
              // Правка договора живёт В СТРОКЕ, а не в шапке карточки: договоров у заказчика
              // несколько, и кнопка в шапке не сказала бы, который из них правят. Отдельной
              // карточки у договора нет намеренно — шесть полей помещаются сюда целиком.
              // Подпись называет договор: «Править» в шапке правит самого заказчика, и два
              // одинаковых слова на экране заставляли гадать, какое из них про договор.
              value={
                <span className='flex flex-wrap items-center gap-2'>
                  {договор.legal_entity_name}
                  {правитьМожно && (
                    <Link
                      to={openEditContractHref(route, договор.id)}
                      className='text-paragraph-xs text-primary-base underline-offset-2 hover:underline тач:inline-flex тач:min-h-11 тач:items-center'
                    >
                      Править договор
                    </Link>
                  )}
                </span>
              }
              note={срокДоговора(договор)}
            />
          ))
        )}
      </FieldList>

      <FieldList
        title={
          объекты.data === undefined
            ? 'Объекты'
            : `Объекты · ${countLabel(объекты.data.total, OBJECT_FORMS)}`
        }
      >
        {объекты.isPending ? (
          <Skeleton className='h-9' />
        ) : объекты.isError ? (
          <ErrorState
            subject='список объектов'
            error={объекты.error as ApiError}
            onRetry={() => void объекты.refetch()}
          />
        ) : (объекты.data?.items ?? []).length === 0 ? (
          <FieldRow
            label='Объектов нет'
            value={null}
            missing='ни один объект этого заказчика ещё не заведён'
          />
        ) : (
          (объекты.data?.items ?? []).map((объект) => (
            <FieldRow
              key={объект.id}
              label={объект.name}
              value={
                <Link
                  to={sitesHref('objects', route.search, объект.id)}
                  className='text-primary-base underline-offset-2 hover:underline тач:inline-flex тач:min-h-11 тач:items-center'
                >
                  {объект.city ?? объект.region ?? 'открыть карточку'}
                </Link>
              }
              note={объект.is_active ? undefined : 'объект закрыт'}
            />
          ))
        )}
      </FieldList>
    </>
  );
}

/** Срок договора словами: подписан и до какого числа действует. */
function срокДоговора(договор: ContractOut): string {
  const подписан =
    договор.signed_at === null ? null : `подписан ${formatDateOnly(договор.signed_at)}`;
  const до =
    договор.date_to === null ? 'без срока окончания' : `по ${formatDateOnly(договор.date_to)}`;
  return [подписан, до].filter((часть) => часть !== null).join(' · ');
}
